import { useState, useMemo, useLayoutEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import profileInfoSelectors from 'models/ProfileInfo/selectors'
import authSelectors from 'models/Authentication/selectors'

import { STRING } from 'resources'
import { hamburgerHalfIcon, crossThinIcon } from 'resources/images'
import { APP_URLS } from 'resources/urls'

import formatPoints from 'utils/format-points'

import NavMenuContent from './NavMenuContent'
import NavMenuProfileAndBalance from './NavMenuProfileAndBalance'

import {
  NavMenuContainer,
  DesktopNavMenu,
  DesktopNavMenuContentContainer,
  MobileNavBarSpacer,
  MobileNavBar,
  MobileNavBarHamburgerButton,
  MobileNavBarHamburgerIcon,
  MobileNavBarReklaimLogoContainer,
  MobileNavBarReklaimLogo,
  MobileNavBarPointsContainer,
  MobileNavBarPoints,
  MobileNavMenu,
  MobileNavMenuBackground,
  MobileNavMenuContentContainer,
  NavMenuHeaderMobileContainer,
  NavMenuHeaderMobileCloseButton,
  NavMenuHeaderMobileCloseButtonIcon,
  NavMenuHeaderDesktopContainer,
} from './styles'

const AUTHENTICATED_HIDE_NAV_MENU_URLS = [
  // Screens where there's login animation/delays
  APP_URLS.SIGN_IN,
  APP_URLS.SIGN_IN_VERIFY_CODE,
  // Authenticated to Unauthenticated screens
  APP_URLS.SIGN_OUT,
  APP_URLS.LOCKED,
  // Landing Page screen
  APP_URLS.LANDING_PAGE,
]

const NavMenu = () => {
  const location = useLocation()
  const balance = useSelector(profileInfoSelectors.getBalance)
  const isAuthenticated = useSelector(authSelectors.getIsAuthenticated)

  const [isMobileNavMenuOpen, setIsMobileNavMenuOpen] = useState(false)

  const openMobileNavMenu = useCallback(() => setIsMobileNavMenuOpen(true), [setIsMobileNavMenuOpen])
  const closeMobileNavMenu = useCallback(() => setIsMobileNavMenuOpen(false), [setIsMobileNavMenuOpen])

  useLayoutEffect(() => {
    window.addEventListener('resize', closeMobileNavMenu)
    return () => window.removeEventListener('resize', closeMobileNavMenu)
  }, [closeMobileNavMenu])

  useLayoutEffect(() => {
    document.body.classList.toggle('modal-open', isMobileNavMenuOpen)
    return () => document.body.classList.remove('modal-open')
  }, [isMobileNavMenuOpen])

  const isNavMenuVisible = useMemo(() => {
    if (!isAuthenticated) {
      return false
    }

    const isPathBlacklisted = AUTHENTICATED_HIDE_NAV_MENU_URLS.includes(location?.pathname)
    return !isPathBlacklisted
  }, [isAuthenticated, location])

  return (
    <>
      <MobileNavBarSpacer className={isNavMenuVisible ? 'visible' : ''} />
      <NavMenuContainer className={isNavMenuVisible ? 'visible' : ''}>
        <DesktopNavMenu>
          <DesktopNavMenuContentContainer>
            <NavMenuHeaderDesktopContainer>
              <NavMenuProfileAndBalance />
            </NavMenuHeaderDesktopContainer>
            <NavMenuContent />
          </DesktopNavMenuContentContainer>
        </DesktopNavMenu>
        <MobileNavBar>
          <MobileNavBarHamburgerButton onClick={openMobileNavMenu} alt={STRING.navMenu.openMenuAlt}>
            <MobileNavBarHamburgerIcon src={hamburgerHalfIcon} />
          </MobileNavBarHamburgerButton>
          <MobileNavBarReklaimLogoContainer>
            <MobileNavBarReklaimLogo alt={STRING.navMenu.logoAlt} />
          </MobileNavBarReklaimLogoContainer>
          <MobileNavBarPointsContainer>
            <MobileNavBarPoints>{formatPoints(balance)}</MobileNavBarPoints>
          </MobileNavBarPointsContainer>
        </MobileNavBar>
        <MobileNavMenu className={isMobileNavMenuOpen ? 'is-open' : ''}>
          <MobileNavMenuBackground onClick={closeMobileNavMenu}>
            <MobileNavMenuContentContainer onClick={(event) => event.stopPropagation()}>
              <NavMenuHeaderMobileContainer>
                <NavMenuProfileAndBalance closeMobileNavMenu={closeMobileNavMenu} />
                <NavMenuHeaderMobileCloseButton onClick={closeMobileNavMenu}>
                  <NavMenuHeaderMobileCloseButtonIcon src={crossThinIcon} alt={STRING.navMenu.closeMenuAlt} />
                </NavMenuHeaderMobileCloseButton>
              </NavMenuHeaderMobileContainer>
              <NavMenuContent closeMobileNavMenu={closeMobileNavMenu} />
            </MobileNavMenuContentContainer>
          </MobileNavMenuBackground>
        </MobileNavMenu>
      </NavMenuContainer>
    </>
  )
}

export default NavMenu
